import { useEffect, useRef, useState } from "react";

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

type FormStatus = "idle" | "sending" | "success" | "error";

export default function ContactSection() {
  const { ref, inView } = useInView();
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value ?? "";
    if (message.length > 500) return;

    setStatus("sending");

    const data = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (el.name) {
        data.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch("https://readdy.ai/api/form/d762pm4bmgf2o8mm70d0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="relative bg-[#080808] py-32 px-6 overflow-hidden">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 font-['Cormorant_Garamond'] text-[18vw] font-light text-white/[0.015] leading-none select-none pointer-events-none whitespace-nowrap">
        CONTACT
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-20 items-start transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>

          {/* Left: info */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-px bg-[#C9A84C]/40" />
              <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">Get In Touch</span>
            </div>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-white mb-6 leading-tight">
              Let&apos;s start a<br />
              <em className="text-[#C9A84C] not-italic">conversation</em>
            </h2>
            <p className="text-white/50 text-base font-light leading-relaxed font-['Outfit'] mb-12">
              Have questions about entering Eastern European markets? 
              Reach out directly and we&apos;ll get back to you within 24 hours.
            </p>

            {/* Contact details */}
            <div className="space-y-6">
              <a
                href="mailto:alex@emcalu.com"
                rel="nofollow"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#C9A84C]/20 group-hover:border-[#C9A84C] transition-colors duration-300">
                  <i className="ri-mail-line text-[#C9A84C]/60 group-hover:text-[#C9A84C] text-lg transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-white/30 text-xs tracking-[0.2em] uppercase font-light font-['Outfit'] mb-1">Email</p>
                  <p className="text-white/70 text-sm font-light font-['Outfit'] group-hover:text-[#C9A84C] transition-colors duration-300">
                    alex@emcalu.com
                  </p>
                </div>
              </a>

              <a
                href="https://emcalu.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#C9A84C]/20 group-hover:border-[#C9A84C] transition-colors duration-300">
                  <i className="ri-global-line text-[#C9A84C]/60 group-hover:text-[#C9A84C] text-lg transition-colors duration-300" />
                </div>
                <div>
                  <p className="text-white/30 text-xs tracking-[0.2em] uppercase font-light font-['Outfit'] mb-1">Website</p>
                  <p className="text-white/70 text-sm font-light font-['Outfit'] group-hover:text-[#C9A84C] transition-colors duration-300">
                    emcalu.com
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[#C9A84C]/20">
                  <i className="ri-map-pin-2-line text-[#C9A84C]/60 text-lg" />
                </div>
                <div>
                  <p className="text-white/30 text-xs tracking-[0.2em] uppercase font-light font-['Outfit'] mb-1">Markets</p>
                  <p className="text-white/70 text-sm font-light font-['Outfit']">
                    Romania · Moldova · CEE
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className={`transition-all duration-700 delay-200 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {status === "success" ? (
              <div className="border border-[#C9A84C]/20 p-12 flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center border border-[#C9A84C] mb-6">
                  <i className="ri-check-line text-[#C9A84C] text-2xl" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-white mb-3">Message sent</h3>
                <p className="text-white/45 text-sm font-light font-['Outfit']">
                  Thank you for reaching out. We&apos;ll reply within 24 hours.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 text-[#C9A84C] text-xs tracking-[0.2em] uppercase font-light font-['Outfit'] hover:text-white transition-colors cursor-pointer"
                >
                  Send another message →
                </button>
              </div>
            ) : (
              <form
                id="emcalu-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="border border-[#C9A84C]/12 p-8 lg:p-10 space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white/35 text-xs tracking-[0.2em] uppercase font-light font-['Outfit'] block mb-2">
                      Name <span className="text-[#C9A84C]">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your name"
                      className="w-full bg-[#0d0d0d] border border-[#C9A84C]/12 text-white/80 text-sm font-light font-['Outfit'] px-4 py-3 focus:outline-none focus:border-[#C9A84C]/40 placeholder:text-white/20 transition-colors duration-300"
                    />
                  </div>
                  <div>
                    <label className="text-white/35 text-xs tracking-[0.2em] uppercase font-light font-['Outfit'] block mb-2">
                      Company <span className="text-[#C9A84C]">*</span>
                    </label>
                    <input
                      type="text"
                      name="company"
                      required
                      placeholder="Your company"
                      className="w-full bg-[#0d0d0d] border border-[#C9A84C]/12 text-white/80 text-sm font-light font-['Outfit'] px-4 py-3 focus:outline-none focus:border-[#C9A84C]/40 placeholder:text-white/20 transition-colors duration-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/35 text-xs tracking-[0.2em] uppercase font-light font-['Outfit'] block mb-2">
                    Email <span className="text-[#C9A84C]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="your@company.com"
                    className="w-full bg-[#0d0d0d] border border-[#C9A84C]/12 text-white/80 text-sm font-light font-['Outfit'] px-4 py-3 focus:outline-none focus:border-[#C9A84C]/40 placeholder:text-white/20 transition-colors duration-300"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white/35 text-xs tracking-[0.2em] uppercase font-light font-['Outfit']">
                      Message <span className="text-[#C9A84C]">*</span>
                    </label>
                    <span className={`text-xs font-light font-['Outfit'] ${charCount > 450 ? "text-[#C9A84C]" : "text-white/25"}`}>
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your expansion plans and target markets..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-[#0d0d0d] border border-[#C9A84C]/12 text-white/80 text-sm font-light font-['Outfit'] px-4 py-3 focus:outline-none focus:border-[#C9A84C]/40 placeholder:text-white/20 transition-colors duration-300 resize-none"
                  />
                </div>

                {status === "error" && (
                  <p className="text-red-400/70 text-xs font-light font-['Outfit']">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full py-4 bg-[#C9A84C] text-[#080808] text-sm font-semibold tracking-[0.2em] uppercase hover:bg-white transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? "Sending..." : "Send Message →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
